/**
 * AI Usage Logs Component
 * Displays OpenAI API usage logs with pagination and sorting
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { formatLogTimestamp } from '../utils/dateUtils';
import './AiUsageLogs.css';

// Source type display names
const SOURCE_LABELS = {
  transcription: 'Transcription',
  title_generation: 'Title Generation',
  prompt_suggestions: 'Prompt Suggestions'
};

function AiUsageLogs({ token, timezone }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);

  // Sorting
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Filters
  const [sourceFilter, setSourceFilter] = useState('');

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: pageSize,
        offset: page * pageSize,
        sortField,
        sortDirection
      });

      if (sourceFilter) {
        params.append('source', sourceFilter);
      }

      const response = await api.get(`/admin/ai-usage?${params}`);

      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching AI usage logs:', err);
      setError('Failed to load AI usage logs');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortField, sortDirection, sourceFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/admin/ai-usage/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching AI usage stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(0); // Reset to first page when sorting
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  // Handle filter change
  const handleSourceFilterChange = (e) => {
    setSourceFilter(e.target.value);
    setPage(0); // Reset to first page when filtering
  };

  // Format date for display
  const formatDate = (dateString) => {
    return formatLogTimestamp(dateString, timezone);
  };

  // Calculate pagination
  const totalPages = Math.ceil(total / pageSize);
  const startRecord = page * pageSize + 1;
  const endRecord = Math.min((page + 1) * pageSize, total);

  return (
    <div className="ai-usage-logs">
      <h2>AI Usage Logs</h2>

      {/* Stats Summary */}
      {stats && (
        <div className="ai-usage-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.totalCalls.toLocaleString()}</div>
            <div className="stat-label">Total API Calls</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalTokens.toLocaleString()}</div>
            <div className="stat-label">Total Tokens</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.uniqueUsers}</div>
            <div className="stat-label">Unique Users</div>
          </div>
          {stats.bySource && Object.entries(stats.bySource).map(([source, data]) => (
            <div className="stat-card" key={source}>
              <div className="stat-value">{data.calls.toLocaleString()}</div>
              <div className="stat-label">{SOURCE_LABELS[source] || source}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="ai-usage-filters">
        <label>
          Filter by Source:
          <select value={sourceFilter} onChange={handleSourceFilterChange}>
            <option value="">All Sources</option>
            <option value="transcription">Transcription</option>
            <option value="title_generation">Title Generation</option>
            <option value="prompt_suggestions">Prompt Suggestions</option>
          </select>
        </label>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <>
          {/* Table */}
          <table className="admin-table ai-usage-table">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort('created_at')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Date/Time{getSortIcon('created_at')}
                </th>
                <th
                  onClick={() => handleSort('user_id')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  User{getSortIcon('user_id')}
                </th>
                <th
                  onClick={() => handleSort('source')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Source{getSortIcon('source')}
                </th>
                <th
                  onClick={() => handleSort('model')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Model{getSortIcon('model')}
                </th>
                <th
                  onClick={() => handleSort('prompt_tokens')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Prompt{getSortIcon('prompt_tokens')}
                </th>
                <th
                  onClick={() => handleSort('completion_tokens')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Completion{getSortIcon('completion_tokens')}
                </th>
                <th
                  onClick={() => handleSort('total_tokens')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Total{getSortIcon('total_tokens')}
                </th>
                <th>Reflection</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">No usage logs found</td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatDate(log.created_at)}</td>
                    <td>{log.username || `User #${log.user_id}`}</td>
                    <td>
                      <span className={`source-badge source-${log.source}`}>
                        {SOURCE_LABELS[log.source] || log.source}
                      </span>
                    </td>
                    <td>{log.model || '-'}</td>
                    <td className="token-cell">{log.prompt_tokens.toLocaleString()}</td>
                    <td className="token-cell">{log.completion_tokens.toLocaleString()}</td>
                    <td className="token-cell">{log.total_tokens.toLocaleString()}</td>
                    <td>{log.reflection_id || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {total > 0 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {startRecord} - {endRecord} of {total.toLocaleString()} records
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => setPage(0)}
                  disabled={page === 0}
                >
                  First
                </button>
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 0}
                >
                  Previous
                </button>
                <span className="page-indicator">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </button>
                <button
                  onClick={() => setPage(totalPages - 1)}
                  disabled={page >= totalPages - 1}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AiUsageLogs;
